import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setUsage(data.usage);
      setLimits(data.limits);
      return data.user;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.access_token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(false);
    setUsage(null);
  };

  const deleteAccount = async () => {
    await api.delete("/auth/account");
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, usage, limits, login, register, logout, deleteAccount, refresh, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
